/**
 * Reading the display contract.
 *
 * `agent_trace_display.json` decides, per tool, which fields lead and which wait
 * behind a disclosure. It is shared with the backend through the `@schemas` alias and
 * pinned to the agent's tool list by a test, so a new tool cannot ship undescribed.
 *
 * The rule is rank, never hide: `inline` and `prose` fields are lifted out, and
 * everything the contract does not name still renders as formatted JSON below.
 */
import contract from '@schemas/agent_trace_display.json'

type Side = { inline?: string[]; prose?: string[] }
export type ToolDisplay = { label?: string; tone?: string; arguments?: Side; result?: Side }

const TOOLS = contract.tools as Record<string, ToolDisplay>

export function displayFor(tool: string): ToolDisplay {
  return TOOLS[tool] ?? (contract.default as ToolDisplay)
}

/** The tools' own rejection envelope: `{error: {code, phase, message, hint, …}}`. */
export const ERROR_DISPLAY = contract.error as Side

export function errorOf(result: unknown): Record<string, unknown> | null {
  if (!result || typeof result !== 'object') return null
  const error = (result as Record<string, unknown>).error
  return error && typeof error === 'object' ? error as Record<string, unknown> : null
}

/** `{result: …}` is the success envelope; anything else is shown as it came. */
export function payloadOf(result: unknown): unknown {
  if (!result || typeof result !== 'object') return result
  const inner = result as Record<string, unknown>
  return 'result' in inner && Object.keys(inner).length === 1 ? inner.result : inner
}

function at(value: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((node, key) => (
    node && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined), value)
}

export type Picked = { named: [string, unknown][]; rest: unknown }

/**
 * Split a payload into the fields the contract names and everything else.
 *
 * `rest` is undefined only when nothing is left over, so a caller can tell "there is
 * more to see" from "you are looking at all of it".
 */
export function pick(value: unknown, paths: string[] = []): Picked {
  if (value === null || value === undefined) return { named: [], rest: undefined }
  if (typeof value !== 'object') return { named: [], rest: value }
  const named: [string, unknown][] = []
  for (const path of paths) {
    const found = at(value, path)
    if (found !== undefined && found !== null && found !== '') named.push([path, found])
  }
  if (Array.isArray(value)) return { named, rest: value }
  const top = new Set(paths.filter(p => !p.includes('.')))
  const rest = Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !top.has(key)))
  return { named, rest: Object.keys(rest).length ? rest : undefined }
}

/** A short, one-line rendering of a value for a header chip. */
export function chipValue(value: unknown): string {
  if (typeof value === 'string') return value.length > 48 ? `${value.slice(0, 48)}…` : value
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`
  if (value && typeof value === 'object') return `${Object.keys(value).length} fields`
  return String(value)
}

/** The last segment of a dotted path: `reference.commit` reads as `commit`. */
export function fieldLabel(path: string): string {
  return (path.split('.').pop() ?? path).replaceAll('_', ' ')
}
